// src/synchronizers/SynchronizerLocutor.js
// VERSÃO COM BED SEPARADA - Sistema Profissional
//
// FLUXO:
// 1. Playlist → ducking FIXO (25%)
// 2. BED → fade in + breathing (sobe/desce nas pausas)
// 3. Locução → volume 100% sempre
// 4. Fim → BED fade out + playlist restaura

export class SynchronizerLocutor {
  constructor(coordinator) {
    this.coordinator = coordinator;
    this.locutorElement = this.createLocutorElement();

    this.isScheduled = false;
    this.isPlaying = false;
    this.isDestroyed = false;

    this.timers = {
      locutor: null,
      recheck: null
    };

    this.currentBlockId = null;
    this.locutorMutex = Promise.resolve();

    // Armazenar dados do agendamento
    this.scheduledData = null;
    this.lastMusicSrc = null;
    this.pausedAt = null;

    // Bind dos event listeners
    this.handleAudioPause = this.handleAudioPause.bind(this);
    this.handleAudioPlay = this.handleAudioPlay.bind(this);
    this.handleAudioSeeked = this.handleAudioSeeked.bind(this);
    this.handleAudioLoadStart = this.handleAudioLoadStart.bind(this);
    this.handleAudioEnded = this.handleAudioEnded.bind(this);

    if (this.coordinator && typeof this.coordinator.registerModule === 'function') {
      this.coordinator.registerModule('locutor', this);
    }

    this.attachAudioListeners();

    console.log('[SyncLocutor] ✅ Inicializado com suporte a BED');
  }

  createLocutorElement() {
    if (typeof document === 'undefined') {
      console.warn('[SyncLocutor] document indisponível');
      return null;
    }

    let el = document.getElementById('locutor-audio-element');
    if (!el) {
      el = document.createElement('audio');
      el.id = 'locutor-audio-element';
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    return el;
  }

  attachAudioListeners() {
    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) {
      console.warn('[SyncLocutor] Sem audioElement para adicionar listeners');
      setTimeout(() => this.attachAudioListeners(), 1000);
      return;
    }

    this.detachAudioListeners();

    audioElement.addEventListener('pause', this.handleAudioPause);
    audioElement.addEventListener('play', this.handleAudioPlay);
    audioElement.addEventListener('seeked', this.handleAudioSeeked);
    audioElement.addEventListener('loadstart', this.handleAudioLoadStart);
    audioElement.addEventListener('ended', this.handleAudioEnded);

    console.log('[SyncLocutor] 🎧 Listeners adicionados');
  }

  detachAudioListeners() {
    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) return;

    audioElement.removeEventListener('pause', this.handleAudioPause);
    audioElement.removeEventListener('play', this.handleAudioPlay);
    audioElement.removeEventListener('seeked', this.handleAudioSeeked);
    audioElement.removeEventListener('loadstart', this.handleAudioLoadStart);
    audioElement.removeEventListener('ended', this.handleAudioEnded);
  }

  handleAudioPause() {
    if (!this.isScheduled || !this.scheduledData) return;

    console.log('[SyncLocutor] ⏸️ Música pausada');
    
    const audioElement = this.coordinator?.audioElement;
    if (audioElement) {
      this.pausedAt = audioElement.currentTime;
    }

    if (this.timers.locutor) {
      clearTimeout(this.timers.locutor);
      this.timers.locutor = null;
    }
  }

  handleAudioPlay() {
    if (!this.isScheduled || !this.scheduledData) return;
    console.log('[SyncLocutor] ▶️ Música retomada - recalculando');
    this.recalcularAgendamento();
  }

  handleAudioSeeked() {
    if (!this.isScheduled || !this.scheduledData) return;
    console.log('[SyncLocutor] ⏩ Seek detectado - recalculando');
    this.recalcularAgendamento();
  }

  handleAudioLoadStart() {
    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) return;

    const newSrc = audioElement.src;
    
    if (this.lastMusicSrc && this.lastMusicSrc !== newSrc) {
      console.log('[SyncLocutor] 🎵 Nova música - cancelando agendamento');
      this.cancel();
      this.scheduledData = null;
    }

    this.lastMusicSrc = newSrc;
  }

  handleAudioEnded() {
    console.log('[SyncLocutor] 🏁 Música terminou');

    if (!this.scheduledData) return;

    // Música acabou mas locutor ainda está agendado — aguarda próxima música carregar
    console.log('[SyncLocutor] ⏭️ Aguardando próxima música para reagendar...');

    if (this.timers.locutor) {
      clearTimeout(this.timers.locutor);
      this.timers.locutor = null;
    }

    // Aguarda próxima música começar e reagenda
    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) return;

    // Captura localmente — handleAudioLoadStart pode limpar this.scheduledData
    // antes de timeupdate disparar, quebrando o reagendamento
    const pendingData = { ...this.scheduledData };

    const onNextSong = () => {
      audioElement.removeEventListener('timeupdate', onNextSong);
      if (pendingData && audioElement.currentTime > 0 && isFinite(audioElement.duration)) {
        console.log('[SyncLocutor] 🎵 Nova música detectada — reagendando locutor');
        const { audioUrl, locutorDuration, pauseMap, bedUrl } = pendingData;
        this.scheduleLocutor(audioUrl, locutorDuration, pauseMap, bedUrl);
      }
    };

    audioElement.addEventListener('timeupdate', onNextSong);

    // Timeout de segurança — se em 60s não veio nova música, descarta
    setTimeout(() => {
      audioElement.removeEventListener('timeupdate', onNextSong);
      console.warn('[SyncLocutor] ⏰ Timeout — descartando locução pendente');
      this.scheduledData = null;
      this.isScheduled = false;
    }, 60000);
  }

  recalcularAgendamento() {
    if (!this.scheduledData) return;

    const { audioUrl, locutorDuration, pauseMap, bedUrl } = this.scheduledData;
    
    if (this.timers.locutor) {
      clearTimeout(this.timers.locutor);
      this.timers.locutor = null;
    }

    const audioElement = this.coordinator?.audioElement;
    if (!audioElement || audioElement.paused) {
      console.log('[SyncLocutor] Música pausada - aguardando');
      return;
    }

    if (!isFinite(audioElement.duration)) {
      console.warn('[SyncLocutor] Duração indefinida - executando agora');
      this.playLocutorNow(audioUrl, pauseMap, bedUrl);
      this.scheduledData = null;
      return;
    }

    const MARGEM_APOS_LOCUTOR = 8;
    const DUCKING_ANTECIPACAO = 2;
    const TEMPO_MINIMO_MUSICA = 30;

    const musicRemaining = audioElement.duration - audioElement.currentTime;
    const tempoParaInicioLocutor = musicRemaining - locutorDuration - MARGEM_APOS_LOCUTOR;
    const tempoParaInicioDucking = tempoParaInicioLocutor - DUCKING_ANTECIPACAO;

    if (musicRemaining < TEMPO_MINIMO_MUSICA || tempoParaInicioDucking <= 0) {
      console.warn(`[SyncLocutor] ⚠️ Tempo insuficiente (${musicRemaining.toFixed(1)}s) — aguardando próxima música`);
      return;
    }

    this.timers.locutor = setTimeout(
      () => this._runScheduledLocutor(audioUrl, locutorDuration, pauseMap, bedUrl),
      tempoParaInicioDucking * 1000
    );

    console.log(`[SyncLocutor] ⏰ Reagendado para ${tempoParaInicioDucking.toFixed(1)}s`);
  }

  // ========== API PÚBLICA ==========

  /**
   * Agenda locutor para o final da música
   * @param {string} audioUrl - URL do áudio do locutor
   * @param {number} locutorDuration - Duração em segundos
   * @param {Array} pauseMap - Mapa de pausas para breathing
   * @param {string} bedUrl - URL da trilha de cama
   */
  async scheduleLocutor(audioUrl, locutorDuration, pauseMap = null, bedUrl = null) {
    if (this.isDestroyed) {
      console.warn('[SyncLocutor] scheduleLocutor chamado após destroy');
      return false;
    }

    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) {
      console.warn('[SyncLocutor] Sem audioElement, executando agora');
      return this.playLocutorNow(audioUrl, pauseMap, bedUrl);
    }

    if (!isFinite(audioElement.duration)) {
      console.warn('[SyncLocutor] Música sem duração, executando agora');
      return this.playLocutorNow(audioUrl, pauseMap, bedUrl);
    }

    const MARGEM_APOS_LOCUTOR = 8; // margem de segurança para sempre terminar antes
    const DUCKING_ANTECIPACAO = 2;
    const TEMPO_MINIMO_MUSICA = 30; // não agenda em músicas com menos de 30s restantes

    const musicDuration = audioElement.duration;
    const musicCurrentTime = audioElement.currentTime;
    const musicRemaining = musicDuration - musicCurrentTime;

    const tempoParaInicioLocutor = musicRemaining - locutorDuration - MARGEM_APOS_LOCUTOR;
    const tempoParaInicioDucking = tempoParaInicioLocutor - DUCKING_ANTECIPACAO;

    console.log('[SyncLocutor] 📊 Calculando temporização:');
    console.log(`  └─ Música total: ${musicDuration.toFixed(1)}s`);
    console.log(`  └─ Música restante: ${musicRemaining.toFixed(1)}s`);
    console.log(`  └─ Locutor: ${locutorDuration.toFixed(1)}s`);
    console.log(`  └─ Início ducking em: ${tempoParaInicioDucking.toFixed(1)}s`);
    console.log(`  └─ PauseMap: ${pauseMap ? pauseMap.length + ' eventos' : 'não'}`);
    console.log(`  └─ BED: ${bedUrl ? 'sim' : 'não'}`);

    // Salvar dados para recálculo
    this.scheduledData = {
      audioUrl,
      locutorDuration,
      pauseMap,
      bedUrl
    };

    this.lastMusicSrc = audioElement.src;
    this.isScheduled = true;

    // Verifica se há tempo suficiente nesta música
    if (musicRemaining < TEMPO_MINIMO_MUSICA || tempoParaInicioDucking <= 0) {
      console.warn(`[SyncLocutor] ⚠️ Música sem tempo suficiente (${musicRemaining.toFixed(1)}s restantes) — aguardando próxima música`);
      return true;
    }

    if (this.timers.locutor) {
      clearTimeout(this.timers.locutor);
    }

    this.timers.locutor = setTimeout(
      () => this._runScheduledLocutor(audioUrl, locutorDuration, pauseMap, bedUrl),
      tempoParaInicioDucking * 1000
    );

    console.log(`[SyncLocutor] ⏰ Agendado para ${tempoParaInicioDucking.toFixed(1)}s`);
    return true;
  }

  /**
   * Toca locutor imediatamente
   */
  async playLocutorNow(audioUrl, pauseMap = null, bedUrl = null) {
    if (this.isDestroyed) {
      console.warn('[SyncLocutor] playLocutorNow chamado após destroy');
      return false;
    }

    return new Promise((resolve) => {
      this.locutorMutex = this.locutorMutex.then(async () => {
        try {
          console.log('[SyncLocutor] 🎙️ Executando locutor');

          // 🔥 Salvar volume original para o breathing usar
          if (this.coordinator?.audioManager?.audioElement) {
            this.coordinator.originalVolume = this.coordinator.audioManager.audioElement.volume;
            this.coordinator.isDucked = true;
          }

          // Preparar áudio
          this.isPlaying = true;
          this.locutorElement.src = audioUrl;
          this.locutorElement.volume = 1.0;
          console.log('[SyncLocutor] 🔗 playLocutorNow audioUrl:', audioUrl);

          // Esperar carregar
          await new Promise((res, rej) => {
            const onCanPlay = () => { cleanup(); res(); };
            const onError = (e) => { cleanup(); console.error('[SyncLocutor] ❌ Erro ao carregar áudio:', audioUrl, e); rej(new Error('Erro ao carregar')); };
            const cleanup = () => {
              this.locutorElement.removeEventListener('canplaythrough', onCanPlay);
              this.locutorElement.removeEventListener('error', onError);
            };
            this.locutorElement.addEventListener('canplaythrough', onCanPlay, { once: true });
            this.locutorElement.addEventListener('error', onError, { once: true });
            this.locutorElement.load();
            setTimeout(() => { cleanup(); res(); }, 5000);
          });

          // 🔥 Iniciar fade 0.5s ANTES do play (em paralelo)
          const firstEvent = pauseMap && pauseMap[0];
          const fadeInDuration = firstEvent?.transition || 0.5;
          const fadeInLevel = firstEvent?.level || 0.40;
          
          // Inicia o fade em paralelo
          const fadePromise = (async () => {
            if (this.coordinator?.audioManager?.audioElement) {
              const playlist = this.coordinator.audioManager.audioElement;
              const targetVolume = fadeInLevel * this.coordinator.originalVolume;
              
              console.log(`[SyncLocutor] 🎭 Fade iniciando (${(fadeInDuration * 1000).toFixed(0)}ms → ${(fadeInLevel * 100).toFixed(0)}%)`);
              
              const steps = Math.max(10, Math.floor(fadeInDuration * 20));
              const stepDelay = (fadeInDuration * 1000) / steps;
              const startVol = playlist.volume;
              const delta = targetVolume - startVol;
              
              for (let i = 0; i < steps; i++) {
                playlist.volume = startVol + (delta * (i + 1) / steps);
                await this._sleep(stepDelay);
              }
            }
          })();

          // 🔥 Espera 0.5s (fade já está rodando) e então dá play
          await this._sleep(500);

          // 🔥 Breathing só para eventos APÓS o primeiro
          if (pauseMap && pauseMap.length > 1 && this.coordinator?.startBreathing) {
            const remainingEvents = pauseMap.slice(1);
            console.log(`[SyncLocutor] 🎭 Breathing pronto (${remainingEvents.length} eventos restantes)`);
            this.coordinator.startBreathing(this.locutorElement, remainingEvents, bedUrl);
          }

          // 🔥 TOCAR (fade ainda pode estar acontecendo)
          console.log('[SyncLocutor] ▶️ Play!');
          await this.locutorElement.play();
          
          // Aguarda fade terminar se ainda não terminou
          await fadePromise;
          
          await this._waitEndedOrTimeout(this.locutorElement);

          // FASE 3: Restaurar
          console.log('[SyncLocutor] 🔈 Restaurando');
          await this.coordinator?.restoreDucking();

          resolve(true);
        } catch (error) {
          console.error('[SyncLocutor] ❌ Erro:', error);
          
          try {
            await this.coordinator?.restoreDucking();
          } catch (e) {}
          
          resolve(false);
        } finally {
          this.isPlaying = false;
          this.isScheduled = false;
          this.scheduledData = null;

          try {
            this.locutorElement.pause();
            this.locutorElement.src = '';
            this.locutorElement.currentTime = 0;
          } catch {}
        }
      });
    });
  }

  cancel() {
    if (this.timers.locutor) {
      clearTimeout(this.timers.locutor);
      this.timers.locutor = null;
    }
    
    if (this.timers.recheck) {
      clearInterval(this.timers.recheck);
      this.timers.recheck = null;
    }
    
    if (this.coordinator?.stopBreathing) {
      this.coordinator.stopBreathing();
    }
    
    this.isScheduled = false;
    this.scheduledData = null;
    console.log('[SyncLocutor] ℹ️ Agendamento cancelado');
  }

  getStatus() {
    let timeUntilLocutor = null;
    
    if (this.isScheduled && this.scheduledData) {
      const audioElement = this.coordinator?.audioElement;
      if (audioElement && isFinite(audioElement.duration)) {
        const remaining = audioElement.duration - audioElement.currentTime;
        const MARGEM = 6;
        const tempoParaInicio = remaining - this.scheduledData.locutorDuration - MARGEM;
        
        if (tempoParaInicio > 0) {
          timeUntilLocutor = tempoParaInicio;
        }
      }
    }
    
    return {
      isScheduled: this.isScheduled,
      isPlaying: this.isPlaying,
      isDestroyed: this.isDestroyed,
      hasScheduledData: !!this.scheduledData,
      hasBed: !!this.scheduledData?.bedUrl,
      timeUntilLocutor,
      isPaused: !!this.pausedAt
    };
  }

  destroy() {
    console.log('[SyncLocutor] 🧹 destroy()');
    this.cancel();
    this.isDestroyed = true;
    this.detachAudioListeners();

    if (this.locutorElement) {
      try {
        this.locutorElement.pause();
        this.locutorElement.src = '';
      } catch {}
    }
  }

  // ========== INTERNOS ==========

  async _runScheduledLocutor(audioUrl, locutorDuration, pauseMap, bedUrl) {
    this.isScheduled = false;
    this.scheduledData = null;

    const audioElement = this.coordinator?.audioElement;
    if (!audioElement) {
      console.warn('[SyncLocutor] Sem audioElement no disparo');
      await this.playLocutorNow(audioUrl, pauseMap, bedUrl);
      return;
    }

    try {
      // 🔥 Salvar volume original para o breathing usar
      if (this.coordinator?.audioManager?.audioElement) {
        this.coordinator.originalVolume = this.coordinator.audioManager.audioElement.volume;
        this.coordinator.isDucked = true;
      }

      console.log('[SyncLocutor] 🎙️ Preparando locutor');
      this.isPlaying = true;

      try {
        this.locutorElement.pause();
        this.locutorElement.currentTime = 0;
      } catch {}

      this.locutorElement.src = audioUrl;
      this.locutorElement.volume = 1.0;
      console.log('[SyncLocutor] 🔗 _runScheduled audioUrl:', audioUrl);

      const loadResult = await this.waitForAudioLoad(this.locutorElement, 10000);
      if (!loadResult.success) {
        console.error('[SyncLocutor] ❌ Falha ao carregar:', audioUrl, loadResult.error);
        throw new Error(loadResult.error || 'Timeout');
      }

      // 🔥 Iniciar fade 0.5s ANTES do play (em paralelo)
      const firstEvent = pauseMap && pauseMap[0];
      const fadeInDuration = firstEvent?.transition || 0.5;
      const fadeInLevel = firstEvent?.level || 0.40;
      
      // Inicia o fade em paralelo
      const fadePromise = (async () => {
        if (this.coordinator?.audioManager?.audioElement) {
          const playlist = this.coordinator.audioManager.audioElement;
          const targetVolume = fadeInLevel * this.coordinator.originalVolume;
          
          console.log(`[SyncLocutor] 🎭 Fade iniciando (${(fadeInDuration * 1000).toFixed(0)}ms → ${(fadeInLevel * 100).toFixed(0)}%)`);
          
          const steps = Math.max(10, Math.floor(fadeInDuration * 20));
          const stepDelay = (fadeInDuration * 1000) / steps;
          const startVol = playlist.volume;
          const delta = targetVolume - startVol;
          
          for (let i = 0; i < steps; i++) {
            playlist.volume = startVol + (delta * (i + 1) / steps);
            await this._sleep(stepDelay);
          }
        }
      })();

      // 🔥 Espera 0.5s (fade já está rodando) e então dá play
      await this._sleep(500);

      // 🔥 Breathing só para os eventos APÓS o primeiro
      if (pauseMap && pauseMap.length > 1 && this.coordinator?.startBreathing) {
        const remainingEvents = pauseMap.slice(1);
        console.log(`[SyncLocutor] 🎭 Breathing pronto (${remainingEvents.length} eventos restantes)`);
        this.coordinator.startBreathing(this.locutorElement, remainingEvents, bedUrl);
      }

      console.log('[SyncLocutor] ▶️ Play!');
      await this.locutorElement.play();
      
      // Aguarda fade terminar se ainda não terminou
      await fadePromise;
      
      await this._waitEndedOrTimeout(this.locutorElement);

      console.log('[SyncLocutor] 🔈 FASE 3: Restaurando');
      await this.coordinator?.restoreDucking();

    } catch (error) {
      console.error('[SyncLocutor] ❌ Erro:', error);
      try {
        await this.coordinator?.restoreDucking();
      } catch (e) {}
    } finally {
      this.isPlaying = false;

      try {
        this.locutorElement.pause();
        this.locutorElement.src = '';
      } catch {}
    }
  }

  waitForAudioLoad(audioElement, timeoutMs = 10000) {
    return new Promise((resolve) => {
      if (!audioElement) {
        return resolve({ success: false, error: 'Elemento inválido' });
      }

      let timeoutId = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        audioElement.removeEventListener('loadeddata', onReady);
        audioElement.removeEventListener('canplaythrough', onReady);
        audioElement.removeEventListener('error', onError);
      };

      const onReady = () => {
        cleanup();
        resolve({ success: true });
      };

      const onError = () => {
        cleanup();
        resolve({ success: false, error: 'Erro ao carregar' });
      };

      timeoutId = setTimeout(() => {
        cleanup();
        resolve({ success: false, error: 'Timeout' });
      }, timeoutMs);

      audioElement.addEventListener('loadeddata', onReady, { once: true });
      audioElement.addEventListener('canplaythrough', onReady, { once: true });
      audioElement.addEventListener('error', onError, { once: true });
    });
  }

  _waitEndedOrTimeout(audioEl, maxExtraSeconds = 5) {
    return new Promise((resolve) => {
      if (!audioEl) return resolve();

      const duration = Number.isFinite(audioEl.duration) && audioEl.duration > 0 
        ? audioEl.duration 
        : 20;

      const timeoutMs = (duration + maxExtraSeconds) * 1000;
      let timeoutId = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        audioEl.removeEventListener('ended', onEnded);
        audioEl.removeEventListener('error', onError);
      };

      const onEnded = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        resolve();
      };

      timeoutId = setTimeout(() => {
        console.warn('[SyncLocutor] ⏱️ Timeout');
        cleanup();
        resolve();
      }, timeoutMs);

      audioEl.addEventListener('ended', onEnded, { once: true });
      audioEl.addEventListener('error', onError, { once: true });
    });
  }

  _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default SynchronizerLocutor;